import React, { useEffect, useMemo, useState } from "react";
import { db, storage, auth } from "@/config/firebase";
import { collection, addDoc, getDocs, serverTimestamp, doc, updateDoc, deleteDoc, query, where } from "firebase/firestore";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { computeBetPoints } from "@/utils/scoring";
import { normalizeMapUrl, buildMapsSearch } from "@/utils/maps";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Tesseract from "tesseract.js";
import { deleteEventById } from "@/services/firestore";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar as CalendarIcon, Plus, MapPin, Clock, Users, ChevronRight, Filter, X, Edit2, Trash2, BarChart3 } from "lucide-react";

// Known gym addresses for better map links
const GYM_ADDRESSES = {
  "DKG": "Dürener Karnevalsgesellschaft, Köln",
  "Weyertal": "Weyertal 39, 50937 Köln",
  "Sportpark Müngersdorf": "Sportpark Müngersdorf, Köln",
  "RheinEnergie": "RheinEnergieStadion, Köln",
  "default": ""
};

const mapsFromLocation = (loc, gymName) => {
  // Try to use gym name first if available
  if (gymName && GYM_ADDRESSES[gymName]) {
    return buildMapsSearch(GYM_ADDRESSES[gymName]);
  }
  // Fallback to location
  return loc ? buildMapsSearch(loc) : "";
};

const GAME_ROLES = [
  { key: 'play', label: 'Spielen', cond: () => true, max: null },
  { key: 'cheer', label: 'Anfeuern', cond: () => true, max: null },
  { key: 'table', label: 'Tisch', cond: (ev) => !!ev.team_home, max: 3 },
  { key: 'coach', label: 'Coach', cond: () => true, max: 1 },
  { key: 'trainer_bag', label: 'Trainertasche', cond: (ev) => !!ev.team_home, max: 1 },
  { key: 'jerseys', label: 'Trikots mitbringen', cond: (ev) => !!ev.team_home, max: null },
  { key: 'balls', label: 'Bälle holen', cond: (ev) => !!ev.team_home, max: null },
  { key: 'passes', label: 'Pässe', cond: (ev) => !ev.team_home, max: 1 },
  { key: 'keys', label: 'Schlüssel', cond: (ev) => !!ev.team_home, max: 1 },
];

// Coin-Hinweis für die Anmeldung (muss zu backend PARTICIPATION_REWARDS passen). Schlüssel = keine Coins.
const SIGNUP_COIN_HINT = { training: 15, balls: 15, jerseys: 25, table: 35, coach: 30 };

const CAL_API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Coin-bringende Rollen je Event-Typ (muss zu backend COIN_ROLES_* passen)
const COIN_ROLE_DEFS = {
  training: [
    { key: 'training', label: 'Teilnahme', coins: 15 },
    { key: 'balls', label: 'Bälle', coins: 15 },
  ],
  game: [
    { key: 'jerseys', label: 'Trikots', coins: 25 },
    { key: 'table', label: 'Tisch', coins: 35 },
    { key: 'coach', label: 'Coach', coins: 30 },
    { key: 'balls', label: 'Bälle', coins: 15 },
  ],
};

// Wett-Coins für Teilnahme/Aufgaben vergeben + korrigieren (jeder darf bearbeiten & bestätigen)
function EventCoinPanel({ ev, isAdmin, users = [] }) {
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [editMode, setEditMode] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const u = auth.currentUser;
      if (!u) return;
      const token = await u.getIdToken();
      const res = await fetch(`${CAL_API_BASE}/api/economy/events/${ev.id}/coin-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setPreview(await res.json());
    } catch (e) { /* noop */ }
  }, [ev.id]);

  React.useEffect(() => { load(); }, [load]);

  const setRole = async (userId, roleKey, active) => {
    setBusy(true);
    setMsg('');
    try {
      const u = auth.currentUser;
      const token = await u.getIdToken();
      const res = await fetch(`${CAL_API_BASE}/api/economy/events/${ev.id}/set-role`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, roleKey, active }),
      });
      const data = await res.json();
      if (res.ok) setPreview(data);
      else setMsg(`Fehler: ${data.detail || res.status}`);
    } catch (e) {
      setMsg(`Fehler: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const award = async () => {
    setBusy(true);
    setMsg('');
    try {
      const u = auth.currentUser;
      const token = await u.getIdToken();
      const res = await fetch(`${CAL_API_BASE}/api/economy/events/${ev.id}/award-coins`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok) {
        setMsg(`✅ ${data.totalAwarded} Coins an ${data.usersAwarded} Personen vergeben`);
        setEditMode(false);
        await load();
      } else {
        setMsg(`Fehler: ${data.detail || res.status}`);
      }
    } catch (e) {
      setMsg(`Fehler: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm('Alle vergebenen Beteiligungs-Coins dieses Events wieder abziehen?')) return;
    setBusy(true);
    setMsg('');
    try {
      const u = auth.currentUser;
      const token = await u.getIdToken();
      const res = await fetch(`${CAL_API_BASE}/api/economy/events/${ev.id}/revoke-coins`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok) {
        setMsg(`↩️ Storniert: ${data.totalReverted} Coins von ${data.usersReverted} Personen`);
        await load();
      } else {
        setMsg(`Fehler: ${data.detail || res.status}`);
      }
    } catch (e) {
      setMsg(`Fehler: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!preview) return null;
  const participants = preview.participants || [];
  const pending = preview.totalPending || 0;
  const anyAwarded = participants.some((p) => (p.alreadyAwardedRoles || []).length > 0);

  // Kompakter Einstieg, wenn noch niemand eingetragen ist
  if (participants.length === 0 && !editMode) {
    return (
      <div className="mt-3">
        <button
          onClick={() => setEditMode(true)}
          className="text-[11px] text-amber-300/70 hover:text-amber-300 underline"
          data-testid={`coin-edit-empty-${ev.id}`}
        >
          ✏️ Beteiligung nachtragen (Coins je Aufgabe)
        </button>
      </div>
    );
  }

  // Edit-Ansicht: Rollen pro Person korrigieren
  const eventType = preview.eventType === 'training' ? 'training' : 'game';
  const roleDefs = COIN_ROLE_DEFS[eventType];
  const assignment = {};
  roleDefs.forEach((rd) => { assignment[rd.key] = []; });
  participants.forEach((p) => {
    (p.roleKeys || []).forEach((rk) => {
      if (assignment[rk]) assignment[rk].push({ userId: p.userId, name: p.displayName });
    });
  });
  const memberOptions = (users || [])
    .map((u) => ({ uid: u.auth_uid || u.id, name: u.display_name || u.name || 'Unbekannt' }))
    .filter((m) => m.uid)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="mt-3 p-3 rounded bg-amber-900/15 border border-amber-500/30" data-testid={`coin-panel-${ev.id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-amber-300">
          🪙 Wett-Coins für Teilnahme (Coins je Aufgabe)
        </p>
        <button
          onClick={() => setEditMode((v) => !v)}
          className={`text-[11px] underline ${editMode ? 'text-green-300' : 'text-amber-300/70 hover:text-amber-300'}`}
          data-testid={`coin-edit-toggle-${ev.id}`}
        >
          {editMode ? 'Fertig' : '✏️ Bearbeiten'}
        </button>
      </div>

      {editMode ? (
        <div className="space-y-2 mb-2">
          <p className="text-[11px] text-amber-200/60">
            Falsch eingetragen? Hier vor dem Gutschreiben korrigieren — jede/r darf ändern.
          </p>
          {roleDefs.map((rd) => {
            const assigned = assignment[rd.key] || [];
            const assignedIds = new Set(assigned.map((x) => x.userId));
            const avail = memberOptions.filter((m) => !assignedIds.has(m.uid));
            return (
              <div key={rd.key} className="rounded bg-black/20 p-2" data-testid={`coin-role-${ev.id}-${rd.key}`}>
                <p className="text-[11px] font-medium text-amber-200 mb-1">{rd.label} <span className="text-amber-400/70">+{rd.coins}</span></p>
                <div className="flex flex-wrap gap-1 mb-1">
                  {assigned.length === 0 && <span className="text-[11px] text-white/30">niemand</span>}
                  {assigned.map((a) => (
                    <span
                      key={a.userId}
                      className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-600/25 border border-amber-500/40 text-amber-100 flex items-center gap-1"
                    >
                      {a.name}
                      <button
                        onClick={() => setRole(a.userId, rd.key, false)}
                        disabled={busy}
                        className="hover:text-rose-300"
                        data-testid={`coin-remove-${ev.id}-${rd.key}-${a.userId}`}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
                <select
                  value=""
                  disabled={busy || avail.length === 0}
                  onChange={(e) => { if (e.target.value) setRole(e.target.value, rd.key, true); }}
                  className="w-full text-[11px] bg-white/5 border border-white/10 rounded px-2 py-1 text-rose-100"
                  data-testid={`coin-add-${ev.id}-${rd.key}`}
                >
                  <option value="">+ Person hinzufügen…</option>
                  {avail.map((m) => (
                    <option key={m.uid} value={m.uid}>{m.name}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      ) : (
        <ul className="text-xs space-y-1 mb-2">
          {participants.map((p) => (
            <li key={p.userId} className="flex items-center justify-between gap-2">
              <span className="text-rose-100/80 truncate">
                {p.displayName} <span className="text-white/40">· {p.roles.join(', ')}</span>
              </span>
              <span className={p.pendingCoins > 0 ? 'text-amber-300 font-medium' : 'text-white/30'}>
                {p.pendingCoins > 0 ? `+${p.pendingCoins}` : '✓ vergeben'}
              </span>
            </li>
          ))}
        </ul>
      )}

      {pending > 0 ? (
        <Button
          size="sm"
          onClick={award}
          disabled={busy}
          className="w-full bg-amber-600 hover:bg-amber-700 text-xs"
          data-testid={`award-coins-${ev.id}`}
        >
          {busy ? '...' : `Coins gutschreiben (+${pending})`}
        </Button>
      ) : (
        participants.length > 0 && <p className="text-xs text-green-400 text-center">Alle Coins bereits vergeben ✓</p>
      )}
      {isAdmin && anyAwarded && (
        <button
          onClick={revoke}
          disabled={busy}
          className="w-full mt-2 text-[11px] text-rose-300/70 hover:text-rose-300 underline"
          data-testid={`revoke-coins-${ev.id}`}
        >
          ↩️ Storno (Coins wieder abziehen)
        </button>
      )}
      {msg && <p className="text-xs text-center mt-2 text-white/70">{msg}</p>}
    </div>
  );
}

// Manueller Coin-Bonus (max 50) pro Event – braucht 3 Zustimmungen (nicht vom Empfänger)
function EventBonusPanel({ ev, isAdmin, users = [] }) {
  const [proposals, setProposals] = React.useState([]);
  const [approvalsNeeded, setApprovalsNeeded] = React.useState(3);
  const [maxAmount, setMaxAmount] = React.useState(50);
  const [showForm, setShowForm] = React.useState(false);
  const [recipient, setRecipient] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const myUid = auth.currentUser?.uid;

  const load = React.useCallback(async () => {
    try {
      const u = auth.currentUser;
      if (!u) return;
      const token = await u.getIdToken();
      const res = await fetch(`${CAL_API_BASE}/api/economy/events/${ev.id}/coin-proposals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setProposals(d.proposals || []);
        setApprovalsNeeded(d.approvalsNeeded || 3);
        setMaxAmount(d.maxAmount || 50);
      }
    } catch (e) { /* noop */ }
  }, [ev.id]);

  React.useEffect(() => { load(); }, [load]);

  const memberOptions = (users || [])
    .map((u) => ({ uid: u.auth_uid || u.id, name: u.display_name || u.name || 'Unbekannt' }))
    .filter((m) => m.uid && m.uid !== myUid)
    .sort((a, b) => a.name.localeCompare(b.name));

  const submit = async () => {
    const amt = parseInt(amount, 10);
    if (!recipient || !amt || amt < 1 || amt > maxAmount) {
      setMsg(`Empfänger wählen & Betrag 1–${maxAmount}`);
      return;
    }
    setBusy(true); setMsg('');
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${CAL_API_BASE}/api/economy/events/${ev.id}/coin-proposals`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipientId: recipient, amount: amt, reason }),
      });
      const data = await res.json();
      if (res.ok) {
        setShowForm(false); setRecipient(''); setAmount(''); setReason('');
        await load();
      } else setMsg(`Fehler: ${data.detail || res.status}`);
    } catch (e) { setMsg(`Fehler: ${e.message}`); } finally { setBusy(false); }
  };

  const approve = async (id) => {
    setBusy(true); setMsg('');
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${CAL_API_BASE}/api/economy/coin-proposals/${id}/approve`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok) await load();
      else setMsg(`Fehler: ${data.detail || res.status}`);
    } catch (e) { setMsg(`Fehler: ${e.message}`); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Diesen Vorschlag zurücknehmen?')) return;
    setBusy(true); setMsg('');
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${CAL_API_BASE}/api/economy/coin-proposals/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) await load();
      else { const data = await res.json(); setMsg(`Fehler: ${data.detail || res.status}`); }
    } catch (e) { setMsg(`Fehler: ${e.message}`); } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 p-3 rounded bg-indigo-900/15 border border-indigo-500/30" data-testid={`bonus-panel-${ev.id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-indigo-300">🎁 Bonus-Coins (max {maxAmount}) · {approvalsNeeded} Zustimmungen nötig</p>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="text-[11px] underline text-indigo-300/80 hover:text-indigo-300"
          data-testid={`bonus-propose-toggle-${ev.id}`}
        >
          {showForm ? 'Abbrechen' : '+ Vorschlagen'}
        </button>
      </div>

      {showForm && (
        <div className="space-y-2 mb-2 p-2 rounded bg-black/20">
          <select
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            className="w-full text-[12px] bg-white/5 border border-white/10 rounded px-2 py-1.5 text-rose-100"
            data-testid={`bonus-recipient-${ev.id}`}
          >
            <option value="">Empfänger wählen…</option>
            {memberOptions.map((m) => <option key={m.uid} value={m.uid}>{m.name}</option>)}
          </select>
          <div className="flex gap-2">
            <input
              type="number" min="1" max={maxAmount} value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Coins (1–${maxAmount})`}
              className="w-28 text-[12px] bg-white/5 border border-white/10 rounded px-2 py-1.5 text-rose-100"
              data-testid={`bonus-amount-${ev.id}`}
            />
            <input
              type="text" value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Grund (optional)"
              className="flex-1 text-[12px] bg-white/5 border border-white/10 rounded px-2 py-1.5 text-rose-100"
              data-testid={`bonus-reason-${ev.id}`}
            />
          </div>
          <Button size="sm" onClick={submit} disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700 text-xs" data-testid={`bonus-submit-${ev.id}`}>
            {busy ? '...' : 'Vorschlag einreichen (du stimmst automatisch zu)'}
          </Button>
        </div>
      )}

      {proposals.length === 0 ? (
        <p className="text-[11px] text-white/30">Noch keine Bonus-Vorschläge.</p>
      ) : (
        <ul className="space-y-1.5">
          {proposals.map((p) => {
            const appr = (p.approvals || []);
            const count = new Set(appr).size;
            const credited = p.status === 'APPROVED';
            const iAmRecipient = p.recipientId === myUid;
            const iApproved = appr.includes(myUid);
            return (
              <li key={p.id} className="text-[12px] p-2 rounded bg-black/20" data-testid={`bonus-item-${p.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-rose-100/90 truncate">
                    <b className="text-indigo-200">{p.amount}</b> für {p.recipientName}
                    {p.reason && <span className="text-white/40"> · {p.reason}</span>}
                  </span>
                  {credited ? (
                    <span className="text-green-400 text-[11px] shrink-0">✓ gutgeschrieben</span>
                  ) : (
                    <span className="text-amber-300 text-[11px] shrink-0">{count}/{approvalsNeeded}</span>
                  )}
                </div>
                <div className="text-[10px] text-white/40 mt-0.5">von {p.proposerName}</div>
                {!credited && (
                  <div className="flex gap-2 mt-1">
                    {!iAmRecipient && !iApproved && (
                      <button onClick={() => approve(p.id)} disabled={busy} className="text-[11px] px-2 py-0.5 rounded bg-green-600/80 hover:bg-green-600 text-white" data-testid={`bonus-approve-${p.id}`}>
                        Zustimmen
                      </button>
                    )}
                    {iApproved && !iAmRecipient && <span className="text-[11px] text-green-400/70">du hast zugestimmt ✓</span>}
                    {iAmRecipient && <span className="text-[11px] text-white/40">du bist Empfänger</span>}
                    {(p.proposerId === myUid || isAdmin) && (
                      <button onClick={() => remove(p.id)} disabled={busy} className="text-[11px] text-rose-300/70 hover:text-rose-300 underline ml-auto" data-testid={`bonus-remove-${p.id}`}>
                        Zurücknehmen
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {msg && <p className="text-[11px] text-center mt-2 text-white/70">{msg}</p>}
    </div>
  );
}


export default function Calendar() {
  const { role, user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({
    title: "",
    type: "game",
    start: "",
    location: "",
    opponent: "",
    gym_name: "",
    league: "",
    team_home: false,
    maps_url: "",
  });
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [finals, setFinals] = useState({ h: "", a: "", t: "", status: "scheduled" });
  const [view, setView] = useState("upcoming");
  const [typeFilter, setTypeFilter] = useState("game_training");
  const [shotFiles, setShotFiles] = useState([]);
  const [shots, setShots] = useState([]);
  const [shotStatus, setShotStatus] = useState("");
  const [shotCounts, setShotCounts] = useState({});
  const [statsCounts, setStatsCounts] = useState({});
  const [signups, setSignups] = useState([]);
  const [viewingStats, setViewingStats] = useState(null);
  const [gameStatsData, setGameStatsData] = useState([]);
  const [gameToDeleteStats, setGameToDeleteStats] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [expandedParticipants, setExpandedParticipants] = useState({}); // Track which events have expanded participants
  const [users, setUsers] = useState([]); // All users for name lookups
  const [eventToDelete, setEventToDelete] = useState(null); // For delete confirmation modal
  const [statsToSave, setStatsToSave] = useState(null); // For stats save confirmation modal
  const [seasonFilter, setSeasonFilter] = useState("2026-27"); // Season filter for games
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_BACKEND_URL);
  
  // CSV Upload & Game Finalize
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [finalScoreHome, setFinalScoreHome] = useState("");
  const [finalScoreAway, setFinalScoreAway] = useState("");
  const [finalTechs, setFinalTechs] = useState("");

  // For manual mapping UI
  const [pendingStats, setPendingStats] = useState([]);

  // Helper: Repariere kaputte Umlaute (temporärer Fix)
  const fixUmlauts = (str) => {
    if (!str) return str;
    return String(str)
      .replace(/Ã¤/g, 'ä')
      .replace(/Ã¼/g, 'ü')
      .replace(/Ã¶/g, 'ö')
      .replace(/Ã„/g, 'Ä')
      .replace(/Ãœ/g, 'Ü')
      .replace(/Ã–/g, 'Ö')
      .replace(/ÃŸ/g, 'ß')
      .replace(/KÄln/g, 'Köln')
      .replace(/SÄdwest/g, 'Südwest')
      .replace(/DÄren/g, 'Düren')
      .replace(/MÄnchen/g, 'München');
  };

  // Helper: Normalize player name (empty/unknown -> null)
  const normalizePlayerName = (name) => {
    if (!name) return null;
    const normalized = String(name).trim().toLowerCase();
    if (normalized === '' || normalized === 'unbekannt' || normalized === 'unknown') {
      return null;
    }
    return String(name).trim();
  };

  // Helper: Parse minutes robustly (supports mm:ss, 32, 32m, 32.25, 32,15)
  const parseMinutes = (input) => {
    if (input === null || input === undefined || input === '') return 0;
    
    const str = String(input).trim();
    
    // Format: "mm:ss" (e.g., "32:45")
    if (str.includes(':')) {
      const parts = str.split(':');
      const minutes = parseInt(parts[0]) || 0;
      const seconds = parseInt(parts[1]) || 0;
      const decimalMinutes = minutes + (seconds / 60);
      // Runde auf 2 Dezimalstellen für saubere Anzeige
      return Math.round(decimalMinutes * 100) / 100;
    }
    
    // Remove trailing 'm' if present (e.g., "32m")
    const cleaned = str.replace(/m$/i, '').trim();
    
    // Handle comma as decimal separator (e.g., "32,15")
    const normalized = cleaned.replace(',', '.');
    
    // Parse as float and round to 2 decimals
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100;
  };

  const load = async () => {
    const snap = await getDocs(collection(db, "events"));
    // Fix kaputte Umlaute beim Laden
    const list = snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        title: fixUmlauts(data.title),
        opponent: fixUmlauts(data.opponent),
        location: fixUmlauts(data.location),
        gym_name: fixUmlauts(data.gym_name),
      };
    });
    
    // Filter games by season
    // For type='game': show only matching seasonId or archived status
    // For other types (training, etc.): always show
    const filtered = list.filter(ev => {
      if (ev.type !== 'game') {
        return true; // Always show non-game events
      }
      
      // For games: filter by season
      if (seasonFilter === '2025-26') {
        // Show archived games from 2025-26
        return ev.seasonId === '2025-26' || ev.archived === true;
      } else {
        // Show current season (2026-27) - non-archived games
        return ev.archived !== true && (ev.seasonId === '2026-27' || !ev.seasonId);
      }
    });
    
    console.log(`📊 Events loaded: ${list.length}, filtered: ${filtered.length} (season: ${seasonFilter})`);
    setEvents(filtered);
    try {
      const all = await getDocs(collection(db, "game_screenshots"));
      const m = {};
      for (const d of all.docs) { const ev = d.data().event_id; if (!ev) continue; m[ev] = (m[ev]||0) + 1; }
      setShotCounts(m);
    } catch {}
    try {
      const gs = await getDocs(collection(db, "game_stats"));
      const ms = {};
      for (const d of gs.docs) { const ev = d.data().game_id; if (!ev) continue; ms[ev] = (ms[ev]||0) + 1; }
      setStatsCounts(ms);
    } catch {}
  };

  useEffect(() => { 
    load(); 
    loadUsers();
  }, [seasonFilter]); // Reload when season filter changes

  const loadUsers = async () => {
    try {
      const usersSnap = await getDocs(collection(db, 'users'));
      const usersList = usersSnap.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));
      setUsers(usersList);
      console.log(`👥 Loaded ${usersList.length} users`);
    } catch (e) {
      console.error('Error loading users:', e);
      setUsers([]);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const rawUrl = form.maps_url || mapsFromLocation(form.location || form.gym_name);
    const maps_url = normalizeMapUrl(rawUrl, form.location || form.gym_name || form.title);
    await addDoc(collection(db, "events"), {
      title: form.title,
      type: form.type,
      start_datetime: form.start ? new Date(form.start) : serverTimestamp(),
      location: form.location,
      opponent: form.opponent,
      gym_name: form.gym_name,
      league: form.league,
      team_home: Boolean(form.team_home),
      maps_url,
      created_by: user?.uid || "",
      created_at: serverTimestamp(),
      status: "scheduled",
    });
    setForm({ title: "", type: "game", start: "", location: "", opponent: "", gym_name: "", league: "", team_home: false, maps_url: "" });
    await load();
  };

  const onOpenMaps = (ev) => {
    const preferred = ev.maps_url || buildMapsSearch(ev.location || ev.gym_name || ev.title);
    const url = normalizeMapUrl(preferred, ev.location || ev.gym_name || ev.title);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const list = useMemo(() => {
    const now = new Date();
    const withDate = events.map((ev) => ({
      ...ev,
      _dt:
        ev.start_datetime?.toDate?.() ||
        (typeof ev.start_datetime === "string" ? new Date(ev.start_datetime) : null),
    }));
    let filtered = withDate;
    if (typeFilter === "game_training") filtered = filtered.filter((e) => e.type === "game" || e.type === "training");
    else if (typeFilter === "game") filtered = filtered.filter((e) => e.type === "game");
    else if (typeFilter === "training") filtered = filtered.filter((e) => e.type === "training");
    if (view === "upcoming") filtered = filtered.filter((e) => (e._dt ? e._dt >= now : true));
    filtered.sort((a, b) => {
      const ta = a._dt ? a._dt.getTime() : 0;
      const tb = b._dt ? b._dt.getTime() : 0;
      return ta - tb;
    });
    return filtered;
  }, [events, view, typeFilter]);

  const finalizeGame = async () => {
    if (!selected) return;
    const refEv = doc(db, "events", selected.id);
    await updateDoc(refEv, { final_home_score: Number(finals.h), final_away_score: Number(finals.a), final_technicals: Number(finals.t), status: finals.status });
    if (finals.status === "final") {
      const bs = await getDocs(query(collection(db, "bets"), where("game_id", "==", selected.id)));
      const season = selected.start_datetime?.toDate?.()?.getFullYear?.() || new Date().getFullYear();
      const totals = new Map();
      const real = { h: Number(finals.h), a: Number(finals.a), t: Number(finals.t) };
      for (const d of bs.docs) {
        const b = { id: d.id, ...d.data() };
        const pred = { h: Number(b.predicted_home_score || 0), a: Number(b.predicted_away_score || 0), t: Number(b.predicted_technicals || 0) };
        const pts = computeBetPoints(pred, real);
        try { await updateDoc(doc(db, "bets", b.id), { points_awarded: pts }); } catch (_) {}
        totals.set(b.user_id, (totals.get(b.user_id) || 0) + pts);
      }
      for (const [uid, points] of totals.entries()) {
        const lid = `${season}_${uid}`;
        try { await updateDoc(doc(db, "bets_leaderboard", lid), { season, user_id: uid, points }); }
        catch(_) { await addDoc(collection(db, "bets_leaderboard"), { season, user_id: uid, points }); }
      }
      const gs = await getDocs(query(collection(db, "game_stats"), where("game_id", "==", selected.id)));
      for (const d of gs.docs) { try { await updateDoc(doc(db, "game_stats", d.id), { status: "final" }); } catch(_) {} }
    }
    await load();
    setSelected(null);
  };

  const loadShots = async (eventId) => {
    try {
      const snap = await getDocs(query(collection(db, "game_screenshots"), where("event_id","==", eventId)));
      const arr = snap.docs.map(d=>({ id:d.id, ...d.data()}));
      setShots(arr);
    } catch { setShots([]); }
  };

  const loadSignups = async (eventId) => {
    try {
      const snap = await getDocs(query(collection(db, 'event_signups'), where('event_id','==', eventId)));
      setSignups(snap.docs.map(d=>({ id:d.id, ...d.data()})));
    } catch { setSignups([]); }
  };

  const loadPendingStats = async (eventId) => {
    try {
      const snap = await getDocs(query(collection(db, 'game_stats'), where('game_id','==', eventId)));
      const rows = snap.docs.map(d=>({ id:d.id, ...d.data()}));
      const requiredFields = ['minutes','points','plus_minus','fouls','twos','threes','free_throws_made','free_throws_att'];
      const pend = rows.filter(r => !r.player_id || requiredFields.some(k => typeof r[k] !== 'number'));
      setPendingStats(pend);
      return rows.length;
    } catch { setPendingStats([]); return 0; }
  };

  const onSelectEvent = async (ev) => {
    // Toggle: Wenn das gleiche Event nochmal geklickt wird, schließen
    if (selected && selected.id === ev.id) {
      setSelected(null);
      return;
    }
    
    setSelected(ev);
    setFinals({ h: ev.final_home_score || "", a: ev.final_away_score || "", t: ev.final_technicals || "", status: ev.status || "scheduled" });
    await loadShots(ev.id);
    await loadSignups(ev.id);
    await loadPendingStats(ev.id);
  };

  const onUploadShots = async (e) => { const files = Array.from(e.target.files||[]); setShotFiles(files); };

  const uploadAll = async () => {
    if (shotFiles.length === 0) { setShotStatus("Keine Dateien ausgewählt"); return; }
    if (!selected || !user) return;
    
    setShotStatus(`Starte Upload von ${shotFiles.length} Datei(en)...`);
    let ok = 0, err = 0;
    
    for (let i = 0; i < shotFiles.length; i++) {
      const f = shotFiles[i];
      const fileName = `${Date.now()}_${f.name.replace(/[^a-zA-Z0-9_.-]/g,'_')}`;
      
      setShotStatus(`📤 Datei ${i+1}/${shotFiles.length}: ${f.name} (${(f.size/1024).toFixed(0)}KB)`);
      
      try {
        // Direkt Base64 Upload (schneller & zuverlässiger)
        const reader = new FileReader();
        
        const base64Data = await new Promise((resolve, reject) => {
          reader.onload = () => {
            setShotStatus(`📤 Datei ${i+1}/${shotFiles.length}: Speichere...`);
            resolve(reader.result);
          };
          reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden"));
          reader.readAsDataURL(f);
        });
        
        // In Firestore speichern
        await addDoc(collection(db, "game_screenshots"), {
          event_id: selected.id,
          uploader_id: user.uid,
          file_name: fileName,
          url: base64Data,
          storage_type: 'base64',
          file_size: f.size,
          mime_type: f.type,
          created_at: serverTimestamp(),
          approved: false
        });
        
        ok++;
        setShotStatus(`✅ ${ok}/${shotFiles.length} erfolgreich hochgeladen`);
        await new Promise(r => setTimeout(r, 500)); // Kurze Pause zwischen Uploads
        
      } catch (e) { 
        err++; 
        console.error(`Upload Fehler für ${f.name}:`, e);
        setShotStatus(`❌ Fehler bei ${f.name}: ${e.message}`);
        await new Promise(r => setTimeout(r, 1000)); // Pause bei Fehler
      }
    }
    
    if (ok > 0) {
      setShotStatus(`✅ Fertig! ${ok} Datei(en) hochgeladen${err > 0 ? `, ${err} fehlgeschlagen` : ''}`);
      setShotFiles([]);
      await loadShots(selected.id);
      await load();
    } else {
      setShotStatus(`❌ Upload fehlgeschlagen. Bitte erneut versuchen.`);
    }
  };

  // OCR parsing helpers
  const normName = (s) => (s||"").toLowerCase().replace(/ß/g,'ss').replace(/\s+/g,' ').trim();
  const parseLinesGeneric = (text) => {
    const lines = (text||"").split(/\n+/).map(s=>s.trim()).filter(Boolean);
    const out = {};
    for (const ln of lines) {
      // Name minutes points
      const m1 = ln.match(/^([A-Za-zÄÖÜäöüß\s\-\.]+?)\s+(\d{1,2}[:]\d{2}|\d{1,2})\s+(\d{1,2})(?:\s+|$)/);
      if (m1) { const nm = normName(m1[1]); const minutes = m1[2]; const pts = Number(m1[3]); out[nm] = { ...(out[nm]||{}), minutes, points: pts }; }
      // 2P/3P/PF pattern (heuristic)
      const m2 = ln.match(/^([A-Za-zÄÖÜäöüß\s\-\.]+?)\s+.*?(\d{1,2})\s*(?:2|2Pkt)\s+.*?(\d{1,2})\s*(?:3|3Pkt).*?(\d{1,2})\s*(?:PF|Fouls)?/i);
      if (m2) { const nm = normName(m2[1]); const twos = Number(m2[2]); const threes = Number(m2[3]); const pf = Number(m2[4]); out[nm] = { ...(out[nm]||{}), twos, threes, pf }; }
      // +/- at end
      const m3 = ln.match(/^([A-Za-zÄÖÜäöüß\s\-\.]+?)\s+.*?([\-+]?\d{1,2})$/);
      if (m3) { const nm = normName(m3[1]); const pm = Number(m3[2]); out[nm] = { ...(out[nm]||{}), plus_minus: pm }; }
      // FW m/a
      const m4 = ln.match(/^([A-Za-zÄÖÜäöüß\s\-\.]+?)\s+.*?(\d{1,2})\/(\d{1,2})/);
      if (m4) { const nm = normName(m4[1]); out[nm] = { ...(out[nm]||{}), free_throws_made: Number(m4[2]), free_throws_att: Number(m4[3]) }; }
    }
    return out;
  };

  const ocrScreens = async () => {
    if (!selected) return;
    setShotStatus("Analysiere Screens…");
    try {
      const usersSnap = await getDocs(collection(db, 'users'));
      const roster = usersSnap.docs.map(d=>({ id:d.id, ...d.data()}));
      const nameToUid = new Map();
      for (const u of roster) { const key = normName(u.display_name || u.name || u.email || ''); if (key) nameToUid.set(key, u.auth_uid||u.id); }
      let combined = {};
      for (const s of shots) {
        const { data } = await Tesseract.recognize(s.url, 'deu+eng');
        const parsed = parseLinesGeneric(data.text||'');
        for (const [k,v] of Object.entries(parsed)) { combined[k] = { ...(combined[k]||{}), ...v }; }
      }
      // Build rows and save
      let saved=0;
      const required = ['minutes','points','plus_minus','fouls','twos','threes','free_throws_made','free_throws_att'];
      const entries = Object.entries(combined).filter(([k])=> k && k!=='player' && !k.startsWith('total'));
      for (const [k, rec] of entries) {
        const uid = nameToUid.get(k);
        const data = {
          player_id: uid || '',
          game_id: selected.id,
          minutes: rec.minutes? (String(rec.minutes).includes(':')? Number(String(rec.minutes).split(':')[0]): Number(rec.minutes||0)) : 0,
          points: Number(rec.points||0),
          plus_minus: Number(rec.plus_minus||0),
          fouls: Number(rec.pf||0),
          twos: Number(rec.twos||0),
          threes: Number(rec.threes||0),
          free_throws_made: Number(rec.free_throws_made||0),
          free_throws_att: Number(rec.free_throws_att||0),
          source: 'ocr-auto',
          uploader_id: user?.uid || '',
        };
        // Save only if at least points present; other fields may be completed manually
        try { await addDoc(collection(db, 'game_stats'), data); saved++; } catch {}
      }
      // stats_ready false by default; require manual completion if any field missing or unmapped
      await updateDoc(doc(db,'events', selected.id), { stats_ready: false });
      setShotStatus(`OCR fertig. Vorläufig gespeichert: ${saved}. Bitte fehlende Zuordnungen vervollständigen.`);
      await loadPendingStats(selected.id);
      await load();
    } catch (e) { setShotStatus('OCR fehlgeschlagen.'); }
  };

  // Signups logic
  const mySignupDocId = (ev, uid) => `${ev.id}_${uid}`;
  const toggleRole = async (roleKey, checked) => {
    if (!user || !selected) return;
    const docId = mySignupDocId(selected, user.uid);
    const ref = doc(db, 'event_signups', docId);
    const mine = signups.find(s=>s.id===docId) || { roles: [], user_id: user.uid, event_id: selected.id };
    let roles = new Set(mine.roles || []);
    if (checked) roles.add(roleKey); else roles.delete(roleKey);
    // enforce caps
    if (roleKey==='table' && checked) {
      const total = signups.filter(s=> (s.roles||[]).includes('table')).length;
      if (total >= 3 && !roles.has('table')) return; // cap reached
    }
    await (await import('firebase/firestore')).setDoc(ref, { roles: Array.from(roles), user_id: user.uid, event_id: selected.id, updated_at: serverTimestamp() }, { merge: true });
    await loadSignups(selected.id);
  };

  const roleCount = (key) => signups.filter(s=> (s.roles||[]).includes(key)).length;
  const amIn = (key) => !!(signups.find(s=>s.user_id===user?.uid && (s.roles||[]).includes(key)));

  // Manual mapping UI handlers
  const updateStatRow = async (rowId, patch) => { await updateDoc(doc(db,'game_stats', rowId), patch); await loadPendingStats(selected.id); };
  const finalizeStats = async () => {
    // Check all rows complete and mapped
    const snap = await getDocs(query(collection(db,'game_stats'), where('game_id','==', selected.id)));
    const rows = snap.docs.map(d=>({ id:d.id, ...d.data()}));
    const required = ['minutes','points','plus_minus','fouls','twos','threes','free_throws_made','free_throws_att','player_id'];
    const incomplete = rows.filter(r => required.some(k => !r[k] && r[k]!==0));
    if (incomplete.length===0 && rows.length>0 && rows.length<=12) {
      await updateDoc(doc(db,'events', selected.id), { stats_ready: true });
      await load();
    }
  };

  const loadGameStats = async (eventId) => {
    try {
      const statsSnap = await getDocs(query(collection(db, 'game_stats'), where('game_id', '==', eventId)));
      const statsList = statsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // User-Namen aus bereits geladener users-Liste verwenden
      const usersMap = {};
      users.forEach(u => {
        usersMap[u.auth_uid || u.id] = u.display_name || u.name || u.email || 'Unbekannt';
      });
      
      // Stats mit Namen anreichern
      const enrichedStats = statsList.map(stat => ({
        ...stat,
        player_name: usersMap[stat.player_id] || 'Unbekannt'
      }));
      
      // Sortiere nach Punkten (höchste zuerst)
      enrichedStats.sort((a, b) => (b.points || 0) - (a.points || 0));
      
      setGameStatsData(enrichedStats);
      setViewingStats(eventId);
    } catch (e) {
      alert(`Fehler beim Laden der Stats: ${e.message}`);
    }
  };
  
  const closeStatsView = () => {
    setViewingStats(null);
    setGameStatsData([]);
  };

  const createUserEvent = async () => {
    if (!form.title || !form.start) {
      alert("Bitte Titel und Datum angeben!");
      return;
    }
    
    try {
      await addDoc(collection(db, "events"), {
        title: form.title,
        type: form.type || "other",
        start_datetime: form.start,
        location: form.location || "",
        gym: form.gym || "",
        opponent: form.opponent || "",
        team_home: form.type === "game" ? true : false,
        team_away: form.type === "game" ? (form.opponent || "") : "",
        maps_url: form.location ? buildMapsSearch(form.location) : "",
        creator_id: user.uid,
        created_at: serverTimestamp(),
        status: "scheduled",
        stats_ready: false
      });
      
      alert("✅ Event erstellt!");
      setShowCreateForm(false);
      setForm({ title: "", type: "game", start: "", location: "", gym: "", team_home: "", team_away: "", opponent: "" });
      await load();
    } catch (e) {
      alert(`❌ Fehler: ${e.message}`);
    }
  };
  
  // Admin: Delete game stats via backend
  const deleteGameStats = async () => {
    if (!gameToDeleteStats || role !== 'admin') return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/delete_game_stats?game_id=${gameToDeleteStats}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      if (data.ok) {
        alert(`✅ ${data.message}`);
        setGameToDeleteStats(null);
        
        // Reload events
        await load();
      } else {
        throw new Error(data.detail || 'Unbekannter Fehler');
      }
    } catch (e) {
      alert(`❌ Fehler beim Löschen: ${e.message}`);
      console.error("Delete stats error:", e);
    }
  };
  
  // Admin: Edit event
  const startEditEvent = (ev) => {
    setEditingEvent(ev.id);
    setEditForm({
      title: ev.title || "",
      type: ev.type || "game",
      start_datetime: ev.start_datetime || "",
      location: ev.location || "",
      gym: ev.gym || "",
      opponent: ev.opponent || "",
      maps_url: ev.maps_url || ""
    });
  };
  
  const saveEditEvent = async () => {
    if (!editingEvent || role !== 'admin') return;
    
    try {
      await updateDoc(doc(db, "events", editingEvent), {
        title: editForm.title,
        type: editForm.type,
        start_datetime: editForm.start_datetime,
        location: editForm.location,
        gym: editForm.gym,
        opponent: editForm.opponent,
        maps_url: editForm.maps_url || buildMapsSearch(editForm.location)
      });
      
      alert("✅ Event aktualisiert!");
      setEditingEvent(null);
      await load();
    } catch (e) {
      alert(`❌ Fehler: ${e.message}`);
    }
  };

  // CSV Upload & Parse
  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    
    // Lade zuerst die User-Liste
    const usersSnap = await getDocs(collection(db, 'users'));
    const users = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    
    // Parse CSV
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      
      if (lines.length < 2) {
        alert("CSV muss mindestens Header + 1 Datenzeile haben");
        return;
      }
      
      // Hilfsfunktion für Name-Matching
      const findMatchingUser = (csvName) => {
        const normalized = csvName.toLowerCase().trim();
        
        // Exakte Übereinstimmung mit display_name, name oder email
        for (const u of users) {
          const displayName = (u.display_name || '').toLowerCase().trim();
          const name = (u.name || '').toLowerCase().trim();
          const email = (u.email || '').split('@')[0].toLowerCase().trim();
          
          if (normalized === displayName || normalized === name || normalized === email) {
            return u.auth_uid || u.id;
          }
        }
        return ""; // Kein Match gefunden
      };
      
      // Parse CSV (einfach mit Komma/Semikolon Trennung)
      const rows = [];
      let autoMatched = 0;
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(/[,;]/);
        if (values.length >= 3) {
          const csvName = values[0]?.trim() || "";
          const playerId = findMatchingUser(csvName);
          if (playerId) autoMatched++;
          
          rows.push({
            name: csvName,
            minutes: values[1]?.trim() || "0", // Keep as string for display, will be parsed on save
            points: parseInt(values[2]?.trim()) || 0,
            plus_minus: parseInt(values[3]?.trim()) || 0,
            fouls: parseInt(values[4]?.trim()) || 0,
            twos: parseInt(values[5]?.trim()) || 0,
            threes: parseInt(values[6]?.trim()) || 0,
            free_throws_made: parseInt(values[7]?.trim()) || 0,
            free_throws_att: parseInt(values[8]?.trim()) || 0,
            player_id: playerId // Auto-matched oder leer
          });
        }
      }
      
      setCsvData(rows);
      setShotStatus(`CSV geladen: ${rows.length} Spieler erkannt, ${autoMatched} automatisch zugeordnet`);
    };
    reader.readAsText(file);
  };

  const updateCsvRow = (index, field, value) => {
    const updated = [...csvData];
    updated[index][field] = value;
    setCsvData(updated);
  };

  const executeSaveStats = async (validatedRows, unresolvedCount, finalScoreHome, finalScoreAway, finalTechs) => {
    console.log(`💾 Executing save for ${validatedRows.length} stats...`);
    setShotStatus("💾 Speichere Stats...");
    
    try {
      // Speichere Stats mit Batch für bessere Performance
      let saved = 0;
      const errors = [];
      
      for (const row of validatedRows) {
        try {
          await addDoc(collection(db, "game_stats"), row);
          saved++;
          console.log(`✅ Saved ${saved}/${validatedRows.length}`);
        } catch (e) {
          console.error('Error saving row:', e);
          errors.push(e.message);
        }
      }
      
      console.log(`📊 Save complete: ${saved}/${validatedRows.length} successful`);
      
      // Update Event mit Ergebnis (nur wenn Scores vorhanden)
      if (finalScoreHome && finalScoreAway) {
        await updateDoc(doc(db, 'events', selected.id), {
          final_home_score: parseInt(finalScoreHome),
          final_away_score: parseInt(finalScoreAway),
          final_technicals: parseInt(finalTechs) || 0,
          status: 'final',
          stats_ready: true
        });
        console.log('✅ Event updated with final scores');
      }
      
      // Success message
      const statusMsg = errors.length > 0 
        ? `⚠️ ${saved} Stats gespeichert, ${errors.length} Fehler` 
        : `✅ ${saved} Stats erfolgreich gespeichert!`;
      
      setShotStatus(statusMsg + (unresolvedCount > 0 ? ` (${unresolvedCount} übersprungen)` : ''));
      
      // Toast für User
      if (errors.length === 0) {
        alert(`✅ Erfolgreich!\n\n${saved} Stats gespeichert${unresolvedCount > 0 ? ` (${unresolvedCount} übersprungen)` : ''}`);
      } else {
        alert(`⚠️ Teilweise erfolgreich\n\n${saved} Stats gespeichert\n${errors.length} Fehler aufgetreten`);
      }
      
      // Clear form
      setCsvData([]);
      setCsvFile(null);
      setFinalScoreHome("");
      setFinalScoreAway("");
      setFinalTechs("");
      await load();
      
    } catch (e) {
      console.error('❌ Critical error saving stats:', e);
      setShotStatus(`❌ Fehler beim Speichern: ${e.message}`);
      alert(`❌ Kritischer Fehler beim Speichern:\n\n${e.message}\n\nDetails in der Browser-Console.`);
    }
  };

  const saveCsvStats = async () => {
    if (!selected || csvData.length === 0) {
      setShotStatus("❌ Keine Daten zum Speichern");
      return;
    }
    
    console.group('💾 Save CSV Stats');
    console.log('Selected event:', selected.id);
    console.log('CSV data rows:', csvData.length);
    
    // Validierung und Normalisierung
    const validatedRows = [];
    const unresolvedRows = [];
    
    for (const row of csvData) {
      const normalizedName = normalizePlayerName(row.name);
      
      // Spieler ohne Namen oder player_id -> unresolved
      if (!normalizedName || !row.player_id || row.player_id === "unknown") {
        unresolvedRows.push({
          name: row.name || 'Unbekannt',
          reason: !normalizedName ? 'Kein Name' : 'Nicht zugeordnet'
        });
        continue;
      }
      
      // Validiere und parse Minuten
      const minutes = parseMinutes(row.minutes);
      
      validatedRows.push({
        game_id: selected.id,
        player_id: row.player_id,
        minutes: minutes,
        points: parseInt(row.points) || 0,
        plus_minus: parseInt(row.plus_minus) || 0,
        fouls: parseInt(row.fouls) || 0,
        twos: parseInt(row.twos) || 0,
        threes: parseInt(row.threes) || 0,
        free_throws_made: parseInt(row.free_throws_made) || 0,
        free_throws_att: parseInt(row.free_throws_att) || 0,
        created_at: serverTimestamp(),
        source: 'csv_import'
      });
    }
    
    console.log('✅ Validated rows:', validatedRows.length);
    console.log('⚠️ Unresolved rows:', unresolvedRows.length);
    
    if (validatedRows.length === 0) {
      alert("❌ Keine gültigen Stats zum Speichern!\n\nBitte ordne mindestens einen Spieler zu.");
      console.groupEnd();
      return;
    }
    
    // Wenn unresolved rows vorhanden sind, zeige Custom-Modal
    if (unresolvedRows.length > 0) {
      setStatsToSave({
        validatedRows,
        unresolvedRows,
        unresolvedCount: unresolvedRows.length,
        finalScoreHome,
        finalScoreAway,
        finalTechs
      });
      console.groupEnd();
      return;
    }
    
    // Keine unresolved rows - direkt speichern
    await executeSaveStats(validatedRows, 0, finalScoreHome, finalScoreAway, finalTechs);
    console.groupEnd();
  };

  return (
    <div className="min-h-screen bg-[#0a0608] text-rose-50 pb-24">
      {/* Header */}
      <header className="px-4 py-3 bg-[#12090c] border-b border-white/5 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="p-1">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="font-semibold flex items-center gap-2">
                <CalendarIcon size={18} className="text-blue-400" />
                Kalender
              </h1>
            </div>
          </div>
          <Button 
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-green-600 hover:bg-green-700 text-sm px-3 py-1.5 h-auto"
          >
            <Plus size={14} className="mr-1" /> Event
          </Button>
        </div>
        
        {/* Filter Tabs */}
        <div className="mt-3 flex gap-1">
          <button
            onClick={() => setView('upcoming')}
            className={`flex-1 py-1.5 text-xs rounded-md transition-colors ${
              view === 'upcoming' ? 'bg-blue-600 text-white' : 'bg-white/5 text-rose-200/60'
            }`}
          >
            Anstehend
          </button>
          <button
            onClick={() => setView('all')}
            className={`flex-1 py-1.5 text-xs rounded-md transition-colors ${
              view === 'all' ? 'bg-blue-600 text-white' : 'bg-white/5 text-rose-200/60'
            }`}
          >
            Alle
          </button>
        </div>
        
        {/* Type Filter */}
        <div className="mt-2 flex gap-1">
          {[
            { value: 'game_training', label: 'Spiele & Training' },
            { value: 'game', label: 'Nur Spiele' },
            { value: 'training', label: 'Nur Training' },
            { value: 'all', label: 'Alles' }
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setTypeFilter(f.value)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                typeFilter === f.value ? 'bg-rose-600/30 text-rose-200 border border-rose-500/30' : 'bg-white/5 text-rose-200/50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        
        {/* Season Filter for Games */}
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-rose-200/70">Saison:</span>
          <select
            value={seasonFilter}
            onChange={(e) => setSeasonFilter(e.target.value)}
            className="bg-[#1a0d11] border border-white/20 rounded px-2 py-1 text-xs text-white"
            data-testid="calendar-season-selector"
          >
            <option value="2026-27">2026-27 (Aktuell)</option>
            <option value="2025-26">2025-26 (Archiv)</option>
          </select>
        </div>
      </header>

      <div className="px-3 py-4 space-y-3">
      
      {/* User: Event erstellen Button */}
      
      {/* Create Event Form */}
      {showCreateForm && (
        <Card className="mb-4 bg-[#1a0d11] border-green-500/30">
          <CardContent className="pt-4">
            <h3 className="text-lg font-semibold mb-3">Neues Event erstellen</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-rose-200/70 block mb-1">Titel *</label>
                <Input 
                  value={form.title} 
                  onChange={(e) => setForm({...form, title: e.target.value})}
                  placeholder="z.B. Training, Spiel vs XY"
                  className="bg-[#2a1419]"
                />
              </div>
              
              <div>
                <label className="text-xs text-rose-200/70 block mb-1">Typ *</label>
                <select 
                  value={form.type} 
                  onChange={(e) => setForm({...form, type: e.target.value})}
                  className="w-full p-2 rounded bg-[#2a1419] text-sm"
                >
                  <option value="game">Spiel</option>
                  <option value="training">Training</option>
                  <option value="other">Sonstiges</option>
                </select>
              </div>
              
              <div>
                <label className="text-xs text-rose-200/70 block mb-1">Datum & Zeit *</label>
                <Input 
                  type="datetime-local"
                  value={form.start} 
                  onChange={(e) => setForm({...form, start: e.target.value})}
                  className="bg-[#2a1419]"
                />
              </div>
              
              <div>
                <label className="text-xs text-rose-200/70 block mb-1">Ort</label>
                <Input 
                  value={form.location} 
                  onChange={(e) => setForm({...form, location: e.target.value})}
                  placeholder="z.B. Weyertal Köln"
                  className="bg-[#2a1419]"
                />
              </div>
              
              {form.type === 'game' && (
                <div>
                  <label className="text-xs text-rose-200/70 block mb-1">Gegner</label>
                  <Input 
                    value={form.opponent} 
                    onChange={(e) => setForm({...form, opponent: e.target.value})}
                    placeholder="z.B. BG Köln"
                    className="bg-[#2a1419]"
                  />
                </div>
              )}
              
              <div className="flex gap-2">
                <Button 
                  onClick={createUserEvent}
                  className="flex-1 bg-green-700 hover:bg-green-800"
                >
                  ✅ Erstellen
                </Button>
                <Button 
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 bg-white/10 hover:bg-white/20"
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Save Stats Confirmation Modal */}
      {statsToSave && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <Card className="bg-[#1a0d11] border-yellow-500/50 max-w-md w-full border-2">
            <CardContent className="pt-4">
              <h3 className="text-xl font-bold mb-3 text-yellow-400">⚠️ Nicht alle Spieler zugeordnet</h3>
              <p className="text-sm text-rose-200/90 mb-3">
                {statsToSave.unresolvedRows.length} Spieler können nicht gespeichert werden:
              </p>
              <div className="bg-[#2a1419] p-3 rounded mb-3 max-h-40 overflow-y-auto">
                {statsToSave.unresolvedRows.map((r, i) => (
                  <p key={i} className="text-xs text-rose-200/80">
                    • {r.name} ({r.reason})
                  </p>
                ))}
              </div>
              <p className="text-sm text-green-400 font-semibold mb-3">
                ✅ {statsToSave.validatedRows.length} Stats werden gespeichert
              </p>
              <p className="text-xs text-rose-200/70 mb-4">
                Die nicht zugeordneten Spieler werden übersprungen.<br/>
                Möchtest du trotzdem fortfahren?
              </p>
              <div className="flex gap-2">
                <Button 
                  onClick={async () => {
                    await executeSaveStats(
                      statsToSave.validatedRows,
                      statsToSave.unresolvedCount,
                      statsToSave.finalScoreHome,
                      statsToSave.finalScoreAway,
                      statsToSave.finalTechs
                    );
                    setStatsToSave(null);
                  }} 
                  className="flex-1 bg-green-700 hover:bg-green-800 text-white font-bold"
                >
                  💾 Ja, speichern!
                </Button>
                <Button 
                  onClick={() => {
                    setShotStatus("❌ Speichern abgebrochen");
                    setStatsToSave(null);
                  }} 
                  className="flex-1 bg-white/10 hover:bg-white/20"
                >
                  Abbrechen
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Delete Event Confirmation Modal */}
      {eventToDelete && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <Card className="bg-[#1a0d11] border-red-500/50 max-w-md w-full border-2">
            <CardContent className="pt-4">
              <h3 className="text-xl font-bold mb-3 text-red-400">🗑️ Event löschen?</h3>
              <p className="text-sm text-rose-200/90 mb-3">
                Möchtest du dieses Event wirklich löschen?
              </p>
              <p className="text-sm font-semibold text-rose-100 mb-3">
                {eventToDelete.title}
              </p>
              <p className="text-xs text-rose-200/70 mb-4">
                ⚠️ Dies kann nicht rückgängig gemacht werden!<br/>
                • Event wird gelöscht<br/>
                • Alle Anmeldungen werden gelöscht
              </p>
              <div className="flex gap-2">
                <Button 
                  onClick={async () => {
                    console.log(`🗑️ Deleting event ${eventToDelete.id}...`);
                    try {
                      const result = await deleteEventById(eventToDelete.id);
                      console.log('Delete result:', result);
                      
                      setEventToDelete(null);
                      
                      if (result.ok) {
                        alert(`✅ ${result.message}`);
                        await load();
                      } else {
                        alert(`❌ ${result.message}`);
                      }
                    } catch (error) {
                      console.error('❌ Delete error:', error);
                      alert(`❌ Fehler: ${error.message}`);
                      setEventToDelete(null);
                    }
                  }} 
                  className="flex-1 bg-red-700 hover:bg-red-800 text-white font-bold"
                >
                  🗑️ Ja, löschen!
                </Button>
                <Button 
                  onClick={() => {
                    console.log('User cancelled delete');
                    setEventToDelete(null);
                  }} 
                  className="flex-1 bg-white/10 hover:bg-white/20"
                >
                  Abbrechen
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Delete Stats Confirmation Dialog */}
      {gameToDeleteStats && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <Card className="bg-[#1a0d11] border-red-500/50 max-w-md w-full border-2">
            <CardContent className="pt-4">
              <h3 className="text-xl font-bold mb-3 text-red-400">⚠️ Stats löschen?</h3>
              <p className="text-sm text-rose-200/90 mb-3">
                Möchtest du alle Spielstatistiken für dieses Event wirklich löschen?
              </p>
              <p className="text-xs text-rose-200/70 mb-4">
                • Alle game_stats werden gelöscht<br/>
                • Event wird zurückgesetzt (stats_ready=false)<br/>
                • Gesamtstatistiken werden bei nächstem Abruf neu berechnet
              </p>
              <div className="flex gap-2">
                <Button onClick={deleteGameStats} className="flex-1 bg-red-700 hover:bg-red-800">
                  🗑️ Ja, löschen
                </Button>
                <Button onClick={() => setGameToDeleteStats(null)} className="flex-1 bg-white/10 hover:bg-white/20">
                  Abbrechen
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Admin: Edit Event Dialog */}
      {editingEvent && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <Card className="bg-[#1a0d11] border-blue-500/30 max-w-md w-full">
            <CardContent className="pt-4">
              <h3 className="text-lg font-bold mb-3">✏️ Event bearbeiten (Admin)</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-rose-200/70 block mb-1">Titel</label>
                  <Input 
                    value={editForm.title} 
                    onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                    className="bg-[#2a1419]"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-rose-200/70 block mb-1">Typ</label>
                  <select 
                    value={editForm.type} 
                    onChange={(e) => setEditForm({...editForm, type: e.target.value})}
                    className="w-full p-2 rounded bg-[#2a1419] text-sm"
                  >
                    <option value="game">Spiel</option>
                    <option value="training">Training</option>
                    <option value="other">Sonstiges</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-xs text-rose-200/70 block mb-1">Datum & Zeit</label>
                  <Input 
                    type="datetime-local"
                    value={editForm.start_datetime} 
                    onChange={(e) => setEditForm({...editForm, start_datetime: e.target.value})}
                    className="bg-[#2a1419]"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-rose-200/70 block mb-1">Ort</label>
                  <Input 
                    value={editForm.location} 
                    onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                    className="bg-[#2a1419]"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-rose-200/70 block mb-1">Halle</label>
                  <Input 
                    value={editForm.gym} 
                    onChange={(e) => setEditForm({...editForm, gym: e.target.value})}
                    className="bg-[#2a1419]"
                  />
                </div>
                
                {editForm.type === 'game' && (
                  <div>
                    <label className="text-xs text-rose-200/70 block mb-1">Gegner</label>
                    <Input 
                      value={editForm.opponent} 
                      onChange={(e) => setEditForm({...editForm, opponent: e.target.value})}
                      className="bg-[#2a1419]"
                    />
                  </div>
                )}
                
                <div>
                  <label className="text-xs text-rose-200/70 block mb-1">Maps URL (optional)</label>
                  <Input 
                    value={editForm.maps_url} 
                    onChange={(e) => setEditForm({...editForm, maps_url: e.target.value})}
                    placeholder="Leer lassen für automatische Generierung"
                    className="bg-[#2a1419]"
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button onClick={saveEditEvent} className="flex-1 bg-blue-700 hover:bg-blue-800">
                    💾 Speichern
                  </Button>
                  <Button onClick={() => setEditingEvent(null)} className="flex-1 bg-white/10 hover:bg-white/20">
                    Abbrechen
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3 px-1">
        <span className="text-xs text-rose-200/50">{list.length} Events</span>
      </div>

      {list.length === 0 ? (
        <Card className="bg-[#12090c] border-white/5">
          <CardContent className="py-6 text-center">
            <CalendarIcon size={32} className="mx-auto mb-3 text-rose-200/30" />
            <p className="text-rose-200/60" data-testid="empty-events">Keine Termine für die aktuelle Ansicht.</p>
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2" data-testid="calendar-list">
          {list.map((ev) => {
            const dt = ev._dt;
            const when = dt ? `${dt.toLocaleDateString('de-DE')} ${dt.toLocaleTimeString('de-DE', { hour: "2-digit", minute: "2-digit" })}` : "";
            const count = shotCounts[ev.id] || 0;
            const hasAll = (count >= 3) && (ev.stats_ready === true);
            const isPast = dt && dt < new Date();
            const isExpanded = selected?.id === ev.id;
            
            const typeColors = {
              game: 'border-l-green-500',
              training: 'border-l-blue-500',
              other: 'border-l-purple-500'
            };
            
            return (
              <li key={ev.id} className={`rounded-lg bg-[#12090c] border border-white/5 overflow-hidden border-l-2 ${typeColors[ev.type] || 'border-l-gray-500'}`} data-testid="calendar-item">
                <div className="p-3 flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">
                        {ev.title || "Ohne Titel"}
                      </p>
                      {hasAll && <span title="Stats vollständig" className="text-green-400 text-xs">✓</span>}
                      {isPast && <span className="bg-rose-900/30 text-rose-300/60 text-[10px] px-1.5 rounded">Vergangen</span>}
                    </div>
                    
                    <div className="flex items-center gap-2 mt-1 text-xs text-rose-200/60">
                      <span className="flex items-center gap-1">
                        <Clock size={11} />
                        {when}
                      </span>
                    </div>
                    
                    {/* Conditional display based on event type */}
                    {ev.type === 'game' && (
                      <div className="mt-1.5 space-y-0.5">
                        <p className="text-xs text-rose-200/70 flex items-center gap-1">
                          <Users size={11} /> vs {ev.opponent || '-'}
                        </p>
                        <p className="text-xs text-rose-200/50 flex items-center gap-1">
                          <MapPin size={11} /> {ev.location || ev.gym_name || "-"}
                        </p>
                        <p className="text-[10px] text-rose-200/40">Screenshots: {count} • Stats: {statsCounts[ev.id]||0}</p>
                      </div>
                    )}
                    
                    {ev.type === 'training' && (
                      <p className="text-xs text-rose-200/50 flex items-center gap-1 mt-1">
                        <MapPin size={11} /> {ev.location || ev.gym_name || "-"}
                      </p>
                    )}
                    
                    {ev.type !== 'game' && ev.type !== 'training' && (
                      <p className="text-xs text-rose-200/80">Ort: {ev.location || ev.gym_name || "-"}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button data-testid="open-maps" onClick={() => onOpenMaps(ev)} className="bg-white/10 hover:bg-white/20 text-xs">Anfahrt</Button>
                    <Button data-testid="calendar-detail" onClick={() => onSelectEvent(ev)} className="bg-white/10 hover:bg-white/20 text-xs">
                      {isExpanded ? '▲ Schließen' : '▼ Details'}
                    </Button>
                  </div>
                </div>
                
                {/* Expanded view - Sign-ups direkt hier */}
                {isExpanded && (
                  <div className="border-t border-white/10 bg-[#160b0f] p-4 space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold">Event Details</p>
                        {role === 'admin' && (
                          <div className="flex gap-2">
                            <Button onClick={() => startEditEvent(ev)} className="bg-blue-700 hover:bg-blue-800 text-xs px-2 py-1">
                              ✏️ Bearbeiten
                            </Button>
                            <Button 
                              onClick={() => {
                                console.log('🔴 DELETE BUTTON CLICKED - Opening modal');
                                setEventToDelete(ev);
                              }} 
                              className="bg-red-700 hover:bg-red-800 text-xs px-2 py-1"
                            >
                              🗑️ Löschen
                            </Button>
                          </div>
                        )}
                      </div>
                      <p className="text-xs">Typ: {selected.type || "-"} • Liga: {selected.league || "-"} • {selected.team_home ? "Heim" : (selected.type==='game'? 'Auswärts':'—')}</p>
                      <p className="text-xs">Halle: {selected.gym_name || '-'}</p>
                      <p className="text-xs">Adresse: {selected.location || '-'}</p>
                    </div>
                    
                    {/* Teilnehmer-Übersicht (für alle sichtbar, ausklappbar) */}
                    <div className="mb-3">
                      <div 
                        className="flex items-center justify-between cursor-pointer p-2 rounded bg-[#1a0d11] border border-white/10"
                        onClick={() => {
                          const newExpanded = {...expandedParticipants};
                          newExpanded[ev.id] = !newExpanded[ev.id];
                          setExpandedParticipants(newExpanded);
                        }}
                      >
                        <p className="text-sm font-semibold">
                          👥 Teilnehmer ({signups.filter(s => s.event_id === ev.id).length})
                        </p>
                        <span className="text-xs">{expandedParticipants[ev.id] ? '▲' : '▼'}</span>
                      </div>
                      
                      {expandedParticipants[ev.id] && (
                        <div className="mt-2 space-y-2">
                          {selected.type === 'training' ? (
                            // Training: Nur "Komme" Liste
                            <div className="bg-[#160b0f] p-2 rounded">
                              <p className="text-xs font-semibold text-green-400 mb-1">✅ Kommt:</p>
                              {signups.filter(s => s.event_id === ev.id && (s.roles || []).includes('training')).length === 0 ? (
                                <p className="text-xs text-rose-200/50">Noch niemand angemeldet</p>
                              ) : (
                                <ul className="text-xs space-y-1">
                                  {signups.filter(s => s.event_id === ev.id && (s.roles || []).includes('training')).map(s => {
                                    // Get user name from users list
                                    const userDoc = users.find(u => u.id === s.user_id);
                                    const userName = userDoc?.display_name || userDoc?.name || 'Unbekannt';
                                    return (
                                      <li key={s.user_id}>• {userName}</li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>
                          ) : (
                            // Spiel: Nach Rollen gruppiert
                            <>
                              {GAME_ROLES.filter(r => r.cond(selected)).map(role => {
                                const participants = signups.filter(s => s.event_id === ev.id && (s.roles || []).includes(role.key));
                                if (participants.length === 0) return null;
                                return (
                                  <div key={role.key} className="bg-[#160b0f] p-2 rounded">
                                    <p className="text-xs font-semibold text-green-400 mb-1">
                                      {role.label}: ({participants.length}{role.max ? `/${role.max}` : ''})
                                    </p>
                                    <ul className="text-xs space-y-1">
                                      {participants.map(s => {
                                        const userDoc = users.find(u => u.id === s.user_id);
                                        const userName = userDoc?.display_name || userDoc?.name || 'Unbekannt';
                                        return (
                                          <li key={s.user_id}>• {userName}</li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                );
                              })}
                              
                              {/* Abgesagt */}
                              {signups.filter(s => s.event_id === ev.id && s.is_out === true).length > 0 && (
                                <div className="bg-[#160b0f] p-2 rounded">
                                  <p className="text-xs font-semibold text-red-400 mb-1">❌ Abgesagt:</p>
                                  <ul className="text-xs space-y-1">
                                    {signups.filter(s => s.event_id === ev.id && s.is_out === true).map(s => {
                                      const userDoc = users.find(u => u.id === s.user_id);
                                      const userName = userDoc?.display_name || userDoc?.name || 'Unbekannt';
                                      return (
                                        <li key={s.user_id}>• {userName}</li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Wett-Coins vergeben (vergangene Events, jeder darf bestätigen) */}
                    {isPast && <EventCoinPanel ev={ev} isAdmin={role === 'admin'} users={users} />}
                    <EventBonusPanel ev={ev} isAdmin={role === 'admin'} users={users} />

                    {/* Signups direkt unter dem Event */}
                    {!isPast && (() => {
                      // Berechtigungsprüfung basierend auf Rolle
                      const userRole = role || 'fan';
                      const canSignUp = 
                        userRole === 'admin' || 
                        userRole === 'player' || 
                        (userRole === 'sparring' && selected.type === 'training');
                      
                      if (!canSignUp) {
                        return (
                          <div className="p-3 rounded bg-yellow-900/20 border border-yellow-500/30">
                            <p className="text-sm text-yellow-400">
                              {userRole === 'fan' 
                                ? '🎉 Als Fan kannst du dich nicht anmelden, aber du bist zum Anfeuern eingeladen!' 
                                : '🏀 Sparringspartner können sich nur für Trainings anmelden'}
                            </p>
                          </div>
                        );
                      }
                      
                      return (
                        <div data-testid="signups-section">
                          <p className="text-sm font-semibold mb-2">📝 Anmeldung</p>
                          {selected.type === 'training' ? (
                            <div className="text-xs space-y-2">
                              <label className="flex items-center gap-2 p-2 rounded bg-[#1a0d11] border border-white/10">
                                <input type="checkbox" checked={amIn('training')} onChange={(e)=>toggleRole('training', e.target.checked)} />
                                Ich komme zum Training <span className="text-amber-400/70">(+15)</span>
                              </label>
                              {selected.gym_name === 'Weyertal' && (
                                <div className="grid grid-cols-2 gap-2">
                                  <label className="flex items-center gap-2 p-2 rounded bg-[#1a0d11] border border-white/10">
                                    <input type="checkbox" checked={amIn('keys')} onChange={(e)=>toggleRole('keys', e.target.checked)} disabled={roleCount('keys')>=1 && !amIn('keys')} />
                                    Schlüssel ({roleCount('keys')}/1)
                                  </label>
                                  <label className="flex items-center gap-2 p-2 rounded bg-[#1a0d11] border border-white/10">
                                    <input type="checkbox" checked={amIn('balls')} onChange={(e)=>toggleRole('balls', e.target.checked)} />
                                    Bälle <span className="text-amber-400/70">(+15)</span> ({roleCount('balls')})
                                  </label>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {GAME_ROLES.filter(r => r.cond(selected)).map(r => (
                                <label key={r.key} className="flex items-center gap-2 p-2 rounded bg-[#1a0d11] border border-white/10">
                                  <input type="checkbox" checked={amIn(r.key)} onChange={(e)=>toggleRole(r.key, e.target.checked)} disabled={r.max && roleCount(r.key)>=r.max && !amIn(r.key)} />
                                  {r.label}{SIGNUP_COIN_HINT[r.key] ? <span className="text-amber-400/70"> (+{SIGNUP_COIN_HINT[r.key]})</span> : null} {r.max? `(${roleCount(r.key)}/${r.max})` : `(${roleCount(r.key)})`}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    
                    {/* Admin: CSV Stats Upload - VEREINFACHT */}
                    {role === "admin" && ev.type === 'game' && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-xs font-semibold mb-2">📊 Spiel abschließen (Admin)</p>
                        
                        {/* Ergebnis eingeben */}
                        <div className="grid grid-cols-3 gap-2 mb-3">
                          <input
                            type="number"
                            placeholder="Heim"
                            value={finalScoreHome}
                            onChange={(e) => setFinalScoreHome(e.target.value)}
                            className="p-2 rounded bg-[#160b0f] border border-white/10 text-xs"
                          />
                          <input
                            type="number"
                            placeholder="Auswärts"
                            value={finalScoreAway}
                            onChange={(e) => setFinalScoreAway(e.target.value)}
                            className="p-2 rounded bg-[#160b0f] border border-white/10 text-xs"
                          />
                          <input
                            type="number"
                            placeholder="T's"
                            value={finalTechs}
                            onChange={(e) => setFinalTechs(e.target.value)}
                            className="p-2 rounded bg-[#160b0f] border border-white/10 text-xs"
                          />
                        </div>
                        
                        {/* CSV Upload */}
                        <div className="space-y-2">
                          <label className="block text-xs text-rose-200/80">
                            Stats als CSV hochladen:
                            <input
                              type="file"
                              accept=".csv"
                              onChange={handleCsvUpload}
                              className="block w-full text-xs mt-1"
                            />
                          </label>
                          <p className="text-xs text-rose-200/60">
                            Format: Name,Minuten,Punkte,+/-,PF,2P,3P,FW_made,FW_att
                          </p>
                        </div>
                        
                        {/* CSV Mapping Tabelle */}
                        {csvData.length > 0 && (
                          <div className="mt-3 overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-[#160b0f]">
                                <tr>
                                  <th className="p-1 text-left">Name (CSV)</th>
                                  <th className="p-1 text-left">Spieler zuordnen</th>
                                  <th className="p-1">Min</th>
                                  <th className="p-1">Pts</th>
                                  <th className="p-1">+/-</th>
                                </tr>
                              </thead>
                              <tbody>
                                {csvData.map((row, idx) => (
                                  <tr key={idx} className={`border-t border-white/10 ${row.player_id && row.player_id !== 'unknown' ? 'bg-green-950/20' : ''}`}>
                                    <td className="p-1">{row.name}</td>
                                    <td className="p-1">
                                      <select
                                        value={row.player_id}
                                        onChange={(e) => updateCsvRow(idx, 'player_id', e.target.value)}
                                        className="w-full p-1 rounded bg-[#160b0f] text-xs"
                                      >
                                        <option value="">Wählen...</option>
                                        <option value="unknown" style={{color: '#fbbf24'}}>🔶 Unbekannt / Später zuordnen</option>
                                        {users.map(u => (
                                          <option key={u.id} value={u.auth_uid || u.id}>
                                            {u.display_name || u.name || u.email}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                    <td className="p-1 text-center">{row.minutes}</td>
                                    <td className="p-1 text-center">{row.points}</td>
                                    <td className="p-1 text-center">{row.plus_minus}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <Button
                              onClick={saveCsvStats}
                              className="mt-2 bg-green-700 hover:bg-green-800 text-xs w-full"
                            >
                              💾 Stats speichern & Spiel finalisieren
                            </Button>
                          </div>
                        )}
                        
                        {shotStatus && <div className="text-xs mt-2 text-rose-200/80">{shotStatus}</div>}
                      </div>
                    )}
                    
                    {/* Stats anzeigen Button (für alle) */}
                    {statsCounts[ev.id] > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <Button 
                          onClick={() => loadGameStats(ev.id)} 
                          className="bg-blue-700 hover:bg-blue-800 text-xs w-full mb-2"
                        >
                          📊 Spielstatistiken anzeigen ({statsCounts[ev.id]} Spieler)
                        </Button>
                        
                        {/* Admin: Stats löschen */}
                        {role === 'admin' && (
                          <Button
                            onClick={() => setGameToDeleteStats(ev.id)}
                            className="bg-red-700 hover:bg-red-800 text-xs w-full"
                          >
                            🗑️ Stats löschen
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      
      {/* Stats-Anzeige Modal */}
      {viewingStats && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={closeStatsView}>
          <Card className="bg-[#1a0d11] border-white/10 max-w-4xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">📊 Spielstatistiken</h3>
                <Button onClick={closeStatsView} className="bg-white/10 hover:bg-white/20 text-xs">✕ Schließen</Button>
              </div>
              
              {gameStatsData.length === 0 ? (
                <p className="text-sm text-rose-200/80">Keine Statistiken vorhanden.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#160b0f] text-rose-200/80">
                      <tr>
                        <th className="p-2 text-left">Platz</th>
                        <th className="p-2 text-left">Spieler</th>
                        <th className="p-2 text-center">Min</th>
                        <th className="p-2 text-center">Pts</th>
                        <th className="p-2 text-center">+/−</th>
                        <th className="p-2 text-center">PF</th>
                        <th className="p-2 text-center">2P</th>
                        <th className="p-2 text-center">3P</th>
                        <th className="p-2 text-center">FW</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gameStatsData.map((stat, idx) => (
                        <tr key={stat.id} className={`border-t border-white/10 ${idx === 0 ? 'bg-yellow-950/20' : ''}`}>
                          <td className="p-2 font-mono text-rose-300">{idx + 1}</td>
                          <td className="p-2 font-medium">{stat.player_name}</td>
                          <td className="p-2 text-center">{stat.minutes || '-'}</td>
                          <td className="p-2 text-center font-bold text-green-400">{stat.points || 0}</td>
                          <td className={`p-2 text-center ${(stat.plus_minus || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {stat.plus_minus > 0 ? '+' : ''}{stat.plus_minus || 0}
                          </td>
                          <td className="p-2 text-center">{stat.fouls || 0}</td>
                          <td className="p-2 text-center">{stat.twos || 0}</td>
                          <td className="p-2 text-center">{stat.threes || 0}</td>
                          <td className="p-2 text-center">{stat.free_throws_made || 0}/{stat.free_throws_att || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Team-Summen */}
                  <div className="mt-4 p-3 rounded bg-[#160b0f] border border-white/10">
                    <p className="text-xs font-semibold mb-2">Team-Gesamt:</p>
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div>
                        <span className="text-rose-200/70">Punkte:</span> 
                        <span className="ml-2 font-bold">{gameStatsData.reduce((sum, s) => sum + (s.points || 0), 0)}</span>
                      </div>
                      <div>
                        <span className="text-rose-200/70">2-Punkte:</span> 
                        <span className="ml-2">{gameStatsData.reduce((sum, s) => sum + (s.twos || 0), 0)}</span>
                      </div>
                      <div>
                        <span className="text-rose-200/70">3-Punkte:</span> 
                        <span className="ml-2">{gameStatsData.reduce((sum, s) => sum + (s.threes || 0), 0)}</span>
                      </div>
                      <div>
                        <span className="text-rose-200/70">Freiwürfe:</span> 
                        <span className="ml-2">
                          {gameStatsData.reduce((sum, s) => sum + (s.free_throws_made || 0), 0)}/
                          {gameStatsData.reduce((sum, s) => sum + (s.free_throws_att || 0), 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      </div>
    </div>
  );
}
